<template>
  <div class="manager-account">
    <div class="page-head">
      <div>
        <h1 class="page-title">账号管理</h1>
        <p class="page-desc">
          管理端登录账号（开发公司内部员工）；与"开通集团管理员账号"（交付客户的用户端账号）是两套独立体系
        </p>
      </div>
      <el-button type="primary" @click="openCreateDrawer">添加账号</el-button>
    </div>

    <el-table
      v-loading="loading"
      :data="managerList"
      border
      style="width: 100%"
    >
      <el-table-column fixed type="index" label="ID" min-width="50" />
      <el-table-column prop="name" label="姓名" min-width="140" />
      <el-table-column prop="username" label="用户名" min-width="160" />
      <el-table-column prop="email" label="邮箱" min-width="200" />
      <el-table-column label="状态" min-width="100">
        <template #default="scope">
          <el-tag
            :type="scope.row.isDeleted === 1 ? 'danger' : 'success'"
            size="small"
          >
            {{ scope.row.isDeleted === 1 ? "已禁用" : "启用中" }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="身份" min-width="110">
        <template #default="scope">
          <el-tag v-if="scope.row.level === '1'" type="warning" size="small">
            超级管理员
          </el-tag>
          <span v-else>普通用户</span>
        </template>
      </el-table-column>
      <el-table-column label="创建时间" min-width="180">
        <template #default="scope">
          {{
            scope.row.createtime
              ? String(scope.row.createtime).replace("T", " ").slice(0, 19)
              : "—"
          }}
        </template>
      </el-table-column>
      <el-table-column label="操作" min-width="120" fixed="right">
        <template #default="scope">
          <!-- 当前登录账号不提供启停操作（后端同样拒绝自禁，双保险） -->
          <el-tag
            v-if="scope.row.id === currentManagerId"
            size="small"
            type="info"
          >
            当前账号
          </el-tag>
          <!-- 非超级管理员无启停权限：任何账号行都不显示操作按钮（后端同样校验） -->
          <span v-else-if="!isSuperAdmin" class="op-none">—</span>
          <el-popconfirm
            v-else
            :title="
              scope.row.isDeleted === 1
                ? '确认启用该账号？'
                : '确认禁用该账号？（禁用后无法登录）'
            "
            @confirm="onToggle(scope.row)"
          >
            <template #reference>
              <el-button
                :type="scope.row.isDeleted === 1 ? 'success' : 'danger'"
                link
                size="small"
              >
                {{ scope.row.isDeleted === 1 ? "启用" : "禁用" }}
              </el-button>
            </template>
          </el-popconfirm>
          <!-- 删除（物理删除不可恢复，独立确认弹窗防误操作；仅超管且非超管目标行可见） -->
          <el-popconfirm
            v-if="isSuperAdmin && scope.row.level !== '1'"
            title="确认删除该账号？（物理删除，不可恢复！）"
            confirm-button-text="删除"
            cancel-button-text="取消"
            @confirm="onDelete(scope.row)"
          >
            <template #reference>
              <el-button type="danger" link size="small"> 删除 </el-button>
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>

    <!-- 添加账号抽屉 -->
    <el-drawer
      v-model="drawer.visible"
      title="添加管理端账号"
      size="420px"
      :close-on-click-modal="!drawer.loading"
      :close-on-press-escape="!drawer.loading"
      :before-close="onDrawerBeforeClose"
      direction="rtl"
      append-to-body
    >
      <el-alert
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 16px"
      >
        <template #title>
          此处创建的是<b>管理端登录账号</b>（开发公司内部使用），创建后即可登录本管理端。
        </template>
      </el-alert>
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="90px"
        status-icon
      >
        <el-form-item label="姓名" prop="name">
          <el-input
            v-model="form.name"
            :disabled="drawer.loading"
            placeholder="请输入姓名"
          />
        </el-form-item>
        <el-form-item label="用户名" prop="username">
          <el-input
            v-model="form.username"
            :disabled="drawer.loading"
            placeholder="5-20位，字母数字"
          />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input
            v-model="form.password"
            type="password"
            show-password
            :disabled="drawer.loading"
            placeholder="8-20位，允许常见符号"
          />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input
            v-model="form.confirmPassword"
            type="password"
            show-password
            :disabled="drawer.loading"
            placeholder="再次输入密码"
          />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input
            v-model="form.email"
            :disabled="drawer.loading"
            placeholder="用于密码重置通知"
          />
        </el-form-item>
        <div class="drawer-footer">
          <el-button type="success" :loading="drawer.loading" @click="onSubmit"
            >创建</el-button
          >
          <el-button type="danger" :disabled="drawer.loading" @click="onCancel"
            >取消</el-button
          >
        </div>
      </el-form>
    </el-drawer>
  </div>
</template>

<script lang="ts" setup>
// 管理端账号管理：manager 表 CRUD（列表 + 添加 + 启用禁用）
import { onMounted, reactive, ref, computed } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import { ElMessage } from "element-plus";
import store from "@/store";
import {
  getManagerListApi,
  createManagerApi,
  toggleManagerApi,
  deleteManagerApi,
  type ManagerAccount,
} from "@/api/manager/managerAccountApi";

/** 当前登录的管理端账号 ID（其所在行不提供启停操作） */
const currentManagerId = computed<string>(
  () => store.getters["auth/managerInfo"]?.id ?? ""
);

/** 是否超级管理员（level=1）：账号启停/删除仅超管可见/可操作 */
const isSuperAdmin = computed<boolean>(
  () => store.getters["auth/managerInfo"]?.level === "1"
);

const loading = ref(false);
const managerList = ref<ManagerAccount[]>([]);

const fetchList = async () => {
  loading.value = true;
  const res = await getManagerListApi();
  if (res) managerList.value = res;
  loading.value = false;
};

onMounted(fetchList);

// ===== 启用/禁用 =====
const onToggle = async (row: ManagerAccount) => {
  const ok = await toggleManagerApi(row.id);
  if (ok) {
    ElMessage.success(row.isDeleted === 1 ? "已启用" : "已禁用");
    fetchList();
  } else {
    ElMessage.error("操作失败");
  }
};

// ===== 删除（物理删除，超管专属） =====
const onDelete = async (row: ManagerAccount) => {
  const ok = await deleteManagerApi(row.id);
  if (ok) {
    ElMessage.success("账号已删除");
    fetchList();
  } else {
    ElMessage.error("删除失败");
  }
};

// ===== 添加账号 =====
const drawer = ref({ visible: false, loading: false });
const formRef = ref<FormInstance>();
const form = reactive({
  name: "",
  username: "",
  password: "",
  confirmPassword: "",
  email: "",
});

const validateConfirm = (
  _: unknown,
  value: string,
  cb: (err?: Error) => void
) => {
  if (!value) return cb(new Error("请再次输入密码"));
  if (value !== form.password) return cb(new Error("两次输入密码不一致"));
  cb();
};

const rules: FormRules = {
  name: [{ required: true, message: "请输入姓名", trigger: "blur" }],
  username: [
    { required: true, message: "请输入用户名", trigger: "blur" },
    {
      pattern: /^[A-Za-z0-9]{5,20}$/,
      message: "用户名仅能包含数字和大小写字母，长度为5~20位",
      trigger: "blur",
    },
  ],
  password: [
    { required: true, message: "请输入密码", trigger: "blur" },
    {
      pattern: /^[0-9A-Za-z!?_.,#@]{8,20}$/,
      message: "密码需 8~20 位，仅可包含数字/字母/! ? _ . , # @",
      trigger: "blur",
    },
  ],
  confirmPassword: [
    { validator: validateConfirm, trigger: ["blur", "change"] },
  ],
  email: [
    { required: true, message: "请输入邮箱", trigger: "blur" },
    { type: "email", message: "邮箱格式不正确", trigger: "blur" },
  ],
};

const openCreateDrawer = () => {
  resetForm();
  drawer.value.visible = true;
};

const resetForm = () => {
  formRef.value?.clearValidate();
  form.name = "";
  form.username = "";
  form.password = "";
  form.confirmPassword = "";
  form.email = "";
};

const onDrawerBeforeClose = (done: () => void) => {
  if (drawer.value.loading) return;
  done();
};

const onCancel = () => {
  if (drawer.value.loading) return;
  drawer.value.visible = false;
};

const onSubmit = async () => {
  if (!formRef.value) return;
  await formRef.value.validate(async (valid) => {
    if (!valid) return;
    drawer.value.loading = true;
    try {
      const ok = await createManagerApi({
        name: form.name,
        username: form.username,
        password: form.password,
        email: form.email,
      });
      if (ok) {
        ElMessage.success("账号创建成功，可立即登录管理端");
        drawer.value.visible = false;
        fetchList();
      } else {
        ElMessage.error("创建失败");
      }
    } catch {
      ElMessage.error("创建失败");
    } finally {
      drawer.value.loading = false;
    }
  });
};
</script>

<style scoped>
.manager-account {
  padding: 16px;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  overflow-y: auto;
}
.page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}
.page-title {
  font-size: 20px;
  margin: 0 0 6px;
}
.page-desc {
  font-size: 12px;
  color: #909399;
  margin: 0;
}
.drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.op-none {
  color: #c0c4cc;
}
</style>
